import type { LiveProfile } from './live-profile';

export const MAX_VIRTUAL_VIEWERS = 999_999;

export type VirtualViewerSettings = {
  base: number;
  fluctuation: number;
  current: number;
};

export const DEFAULT_VIRTUAL_VIEWERS: VirtualViewerSettings = {
  base: 0,
  fluctuation: 0,
  current: 0,
};

function normalizeCount(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(MAX_VIRTUAL_VIEWERS, Math.max(0, Math.round(value)))
    : fallback;
}

export function normalizeVirtualViewerSettings(value: unknown): VirtualViewerSettings {
  if (!value || typeof value !== 'object') return DEFAULT_VIRTUAL_VIEWERS;
  const input = value as Partial<VirtualViewerSettings>;
  const base = normalizeCount(input.base, 0);
  const fluctuation = Math.min(base, normalizeCount(input.fluctuation, 0));
  const lower = base - fluctuation;
  const upper = base + fluctuation;
  const current = Math.min(upper, Math.max(lower, normalizeCount(input.current, base)));
  return { base, fluctuation, current };
}

export function createLiveHostMetadata(
  profile: LiveProfile,
  virtualViewers: VirtualViewerSettings,
) {
  return JSON.stringify({
    ...profile,
    virtualViewers: normalizeVirtualViewerSettings(virtualViewers),
  });
}

export function parseVirtualViewerMetadata(metadata?: string): VirtualViewerSettings {
  if (!metadata) return DEFAULT_VIRTUAL_VIEWERS;
  try {
    const value = JSON.parse(metadata) as { virtualViewers?: unknown };
    return normalizeVirtualViewerSettings(value.virtualViewers);
  } catch {
    return DEFAULT_VIRTUAL_VIEWERS;
  }
}

export function getDisplayedViewerCount(actualViewerCount: number, virtualViewerCount: number) {
  const actual = Number.isFinite(actualViewerCount)
    ? Math.max(0, Math.round(actualViewerCount))
    : 0;
  const virtual = Number.isFinite(virtualViewerCount)
    ? Math.max(0, Math.round(virtualViewerCount))
    : 0;
  return actual + virtual;
}

export function nextVirtualViewerCount(
  settings: VirtualViewerSettings,
  random = Math.random,
): number {
  const normalized = normalizeVirtualViewerSettings(settings);
  if (normalized.fluctuation === 0) return normalized.base;

  const step = Math.max(1, Math.ceil(normalized.fluctuation / 3));
  const randomValue = Math.min(0.999999, Math.max(0, random()));
  const delta = Math.floor(randomValue * (step * 2 + 1)) - step;
  return Math.min(
    normalized.base + normalized.fluctuation,
    Math.max(normalized.base - normalized.fluctuation, normalized.current + delta),
  );
}
